import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';
import {
  Plus, Settings, Trash2, Users, Coffee, Calendar, FileText, Mail,
  Search, GripVertical, Power, UserPlus, UserCheck, UserX
} from 'lucide-react';
import { calcTemplateWeekHours } from '@/utils/labourHours';

export function UsersTab({ ctx }) {
  const {
    users, user, userFilter, setUserFilter, fetchUsers,
    setShowCreateUser, handleEditUser, handleCreateUserClick, handleDeleteUser,
    handleToggleUserActive
  } = ctx;
  const shownUsers = users.filter(u =>
    userFilter === 'all' ? true : userFilter === 'active' ? u.is_active !== false : u.is_active === false
  );

  return (
    <TabsContent value="users">
      <div className="bg-white rounded-xl shadow-md border border-stone-200 p-6">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-stone-800">User Management</h2>
            <p className="text-sm text-stone-600 mt-1">Create and manage staff accounts</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 bg-stone-100 rounded-lg p-1">
              <button
                onClick={() => setUserFilter('all')} data-testid="user-filter-all"
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${userFilter === 'all' ? 'bg-white shadow text-stone-800' : 'text-stone-600 hover:text-stone-800'}`}
              >All</button>
              <button
                onClick={() => setUserFilter('active')} data-testid="user-filter-active"
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition flex items-center gap-1 ${userFilter === 'active' ? 'bg-white shadow text-green-700' : 'text-stone-600 hover:text-stone-800'}`}
              ><UserCheck className="w-3 h-3" /> Active</button>
              <button
                onClick={() => setUserFilter('inactive')} data-testid="user-filter-inactive"
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition flex items-center gap-1 ${userFilter === 'inactive' ? 'bg-white shadow text-red-700' : 'text-stone-600 hover:text-stone-800'}`}
              ><UserX className="w-3 h-3" /> Inactive</button>
            </div>
            <Button onClick={() => (handleCreateUserClick ? handleCreateUserClick() : setShowCreateUser(true))} data-testid="create-user-button" className="bg-gradient-to-r from-gray-600 to-gray-700 hover:bg-gray-700 text-white">
              <UserPlus className="w-4 h-4 mr-2" /> Create User
            </Button>
          </div>
        </div>

        {shownUsers.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-100 border-b border-stone-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">Username</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">Emp Code</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">Branch/Branches</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">Allowed Roles</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-200">
                {shownUsers.map(u => (
                  <tr key={u.id} data-testid={`user-row-${u.username}`} className={`hover:bg-stone-50 ${u.is_active === false ? 'opacity-60 bg-stone-50' : ''}`}>
                    <td className="px-4 py-4">
                      {u.id !== user.id ? (
                        <button onClick={() => handleToggleUserActive(u.id, u.username)}
                          className={`p-1.5 rounded-full transition ${u.is_active !== false ? 'bg-green-100 text-green-600 hover:bg-green-200' : 'bg-red-100 text-red-600 hover:bg-red-200'}`}
                          title={u.is_active !== false ? 'Click to deactivate' : 'Click to activate'}>
                          <Power className="w-4 h-4" />
                        </button>
                      ) : <span className="text-xs text-stone-400">You</span>}
                    </td>
                    <td className="px-4 py-4 text-sm text-stone-800">
                      {u.id !== user.id ? (
                        <button type="button" onClick={() => handleEditUser(u)} data-testid={`user-name-link-${u.username}`}
                          className="font-medium text-left hover:text-blue-700 hover:underline underline-offset-2">
                          {u.first_name || u.last_name ? `${u.first_name || ''} ${u.last_name || ''}`.trim() : u.username}
                        </button>
                      ) : (u.first_name || u.last_name ? <span className="font-medium">{u.first_name} {u.last_name}</span> : <span className="text-stone-400">-</span>)}
                    </td>
                    <td className="px-4 py-4 text-sm text-stone-700">
                      {u.id !== user.id ? (
                        <button type="button" onClick={() => handleEditUser(u)} data-testid={`user-username-link-${u.username}`}
                          className="text-left hover:text-blue-700 hover:underline underline-offset-2">{u.username}</button>
                      ) : u.username}
                    </td>
                    <td className="px-4 py-4 text-sm text-stone-700 font-mono">{u.employee_code || '-'}</td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${u.role === 'admin' ? 'bg-purple-100 text-purple-800' : u.role === 'manager' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>{u.role}</span>
                      {u.include_in_gratuities && (
                        <span
                          className="ml-1 inline-flex px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800"
                          title="Tip-eligible — counted in gratuity apportionment"
                          data-testid={`user-gratuity-chip-${u.username}`}>
                          Tip
                        </span>
                      )}
                      {u.role === 'staff' && u.include_in_gratuities === false && (
                        <span
                          className="ml-1 inline-flex px-2 py-1 text-xs font-medium rounded-full bg-stone-200 text-stone-700"
                          title="Excluded from gratuities — the toggle is OFF on this user's profile"
                          data-testid={`user-gratuity-excluded-chip-${u.username}`}>
                          No Tip
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-stone-700">{u.role === 'manager' ? u.branch : u.branches?.join(', ') || '-'}</td>
                    <td className="px-4 py-4 text-sm text-stone-700">{u.allowed_roles?.join(', ') || '-'}</td>
                    <td className="px-4 py-4">
                      {u.id !== user.id && (
                        <div className="flex gap-2">
                          <Button onClick={() => handleEditUser(u)} data-testid={`edit-user-${u.username}`} variant="ghost" size="sm" className="text-blue-600 hover:text-blue-800 hover:bg-blue-50">
                            <Settings className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={async () => {
                              if (!u.email) { toast.error('This user has no email address.'); return; }
                              if (!window.confirm(`Send a welcome email with a password-setup link to ${u.email}?`)) return;
                              try {
                                await axios.post(`${API}/account/send-welcome/${u.id}`);
                                toast.success(`Welcome email sent to ${u.email}`);
                              } catch (e) {
                                toast.error(e.response?.data?.detail || 'Send failed');
                              }
                            }}
                            data-testid={`send-welcome-${u.username}`}
                            variant="ghost" size="sm"
                            className="text-amber-700 hover:text-amber-900 hover:bg-amber-50"
                            title="Send welcome / password-setup email"
                          >
                            <Mail className="w-4 h-4" />
                          </Button>
                          <Button onClick={() => handleDeleteUser(u.id)} data-testid={`delete-user-${u.username}`} variant="ghost" size="sm" className="text-red-600 hover:text-red-800 hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-stone-500">
            <Users className="w-12 h-12 mx-auto mb-3 text-stone-400" />
            <p>No users found</p>
          </div>
        )}
      </div>
    </TabsContent>
  );
}

export function TemplatesTab({ ctx }) {
  const {
    shiftTemplates, selectedBranch, setSelectedBranch, branches,
    setShowCreateTemplate, handleEditTemplate, handleDeleteTemplate,
    handleToggleTemplate, handleDragStart, handleDragOver, handleDrop,
    handleDragEnd, draggedTemplate,
    templateWeeks, setShowCreateTemplateWeek, setShowEditTemplateWeek,
    handleDeleteTemplateWeek, handleSaveWeekAsTemplate, setShowSaveWeekAsTemplate
  } = ctx;

  const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  return (
    <TabsContent value="templates">
      {/* Template Weeks Section */}
      <div className="bg-white rounded-xl shadow-md border border-stone-200 p-6 mb-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-stone-800">Template Weeks</h2>
            <p className="text-sm text-stone-600 mt-1">Saved weekly shift layouts that can be applied to any week</p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedBranch} onValueChange={setSelectedBranch}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                {branches.map(branch => (<SelectItem key={branch.id} value={branch.name}>{branch.name}</SelectItem>))}
              </SelectContent>
            </Select>
            <Button onClick={() => setShowCreateTemplateWeek(true)} data-testid="create-template-week-btn" className="bg-gradient-to-r from-gray-600 to-gray-700 hover:bg-gray-700 text-white">
              <Plus className="w-4 h-4 mr-2" /> New Template Week
            </Button>
          </div>
        </div>

        {(templateWeeks || []).filter(tw => tw.branch === selectedBranch).length > 0 ? (
          <div className="space-y-3">
            {templateWeeks.filter(tw => tw.branch === selectedBranch).map(tw => (
              <div key={tw.id} data-testid={`template-week-${tw.id}`} className="border border-stone-200 rounded-lg p-4 hover:shadow-md transition">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="font-semibold text-stone-800 text-lg">{tw.name}</div>
                    <div className="flex items-center gap-3 mt-0.5">
                      <span className="text-xs text-stone-500">{tw.branch} &middot; {tw.shifts?.length || 0} shifts</span>
                      <span className="text-xs font-semibold text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full" data-testid={`tw-hours-${tw.id}`}>
                        {calcTemplateWeekHours(tw.shifts)}h labour
                      </span>
                    </div>
                    <div className="grid grid-cols-7 gap-1 mt-3">
                      {DAY_NAMES.map((day, idx) => {
                        const dayShifts = (tw.shifts || []).filter(s => s.day_of_week === idx);
                        return (
                          <div key={idx} className="bg-stone-50 rounded p-1.5 min-h-[40px]">
                            <div className="text-[10px] font-semibold text-stone-600 mb-0.5">{day}</div>
                            {dayShifts.length > 0 ? dayShifts.map((s, i) => (
                              <div key={i} className="text-[10px] bg-white border border-stone-200 rounded px-1 py-0.5 mb-0.5">
                                <span className="font-semibold">{s.shift_code}</span> {s.start_time}-{s.end_time} <span className="text-stone-400">x{s.max_capacity}</span>
                              </div>
                            )) : <div className="text-[10px] text-stone-400 italic">—</div>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button onClick={() => setShowEditTemplateWeek(tw)} variant="ghost" size="sm" className="text-blue-600 hover:bg-blue-50" data-testid={`edit-tw-${tw.id}`}>
                      <Settings className="w-4 h-4" />
                    </Button>
                    <Button onClick={() => handleDeleteTemplateWeek(tw.id, tw.name)} variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" data-testid={`delete-tw-${tw.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-stone-500">
            <Calendar className="w-10 h-10 mx-auto mb-2 text-stone-400" />
            <p className="text-sm">No template weeks for {selectedBranch}. Save a week or create one from scratch.</p>
          </div>
        )}
      </div>

      {/* Shift Templates Section (individual templates) */}
      <div className="bg-white rounded-xl shadow-md border border-stone-200 p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-stone-800">Shift Templates</h2>
            <p className="text-sm text-stone-600 mt-1">Individual shift definitions used in template weeks</p>
          </div>
          <Button onClick={() => setShowCreateTemplate(true)} data-testid="create-template-button" className="bg-gradient-to-r from-gray-600 to-gray-700 hover:bg-gray-700 text-white">
            <Plus className="w-4 h-4 mr-2" /> New Template
          </Button>
        </div>

        {shiftTemplates.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-100 border-b-2 border-stone-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase w-12"></th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase">Shift Code</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase">Role</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase">Time</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-stone-600 uppercase">Lunch</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-stone-600 uppercase">Enabled</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-stone-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-200">
                {shiftTemplates.map(template => (
                  <tr key={template.id} draggable onDragStart={(e) => handleDragStart(e, template)} onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, template)} onDragEnd={handleDragEnd} data-testid={`template-row-${template.shift_code}`} className={`hover:bg-stone-50 transition cursor-move ${draggedTemplate?.id === template.id ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-4"><GripVertical className="w-5 h-5 text-stone-400" /></td>
                    <td className="px-4 py-4"><span className="inline-flex px-3 py-1 bg-gray-100 text-gray-800 font-semibold rounded-lg text-sm">{template.shift_code}</span></td>
                    <td className="px-4 py-4 text-sm text-stone-700">{template.role_name}</td>
                    <td className="px-4 py-4 text-sm text-stone-700">{template.start_time} - {template.end_time}</td>
                    <td className="px-4 py-4 text-sm text-stone-700">{template.lunch_minutes} min</td>
                    <td className="px-4 py-4 text-center">
                      <Switch checked={template.enabled} onCheckedChange={(checked) => handleToggleTemplate(template.id, checked)} data-testid={`template-toggle-${template.shift_code}`} />
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button onClick={() => handleEditTemplate(template)} data-testid={`edit-template-${template.shift_code}`} variant="ghost" size="sm" className="text-blue-600 hover:bg-blue-50"><Settings className="w-4 h-4" /></Button>
                        <Button onClick={() => handleDeleteTemplate(template.id, template.shift_code)} data-testid={`delete-template-${template.shift_code}`} variant="ghost" size="sm" className="text-red-600 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12 text-stone-500">
            <Coffee className="w-12 h-12 mx-auto mb-3 text-stone-400" />
            <p>No templates yet. Create your first shift template!</p>
          </div>
        )}
      </div>
    </TabsContent>
  );
}

export function RolesTab({ ctx }) {
  const { systemRoles, setShowCreateRole, handleDeleteRole } = ctx;

  return (
    <TabsContent value="roles">
      <div className="bg-white rounded-xl shadow-md border border-stone-200 p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-stone-800">Staff Roles Management</h2>
            <p className="text-sm text-stone-600 mt-1">Manage system-wide staff roles and shift codes</p>
          </div>
          <Button onClick={() => setShowCreateRole(true)} data-testid="create-role-button" className="bg-gradient-to-r from-gray-600 to-gray-700 hover:bg-gray-700 text-white">
            <Plus className="w-4 h-4 mr-2" /> Add Role
          </Button>
        </div>
        {systemRoles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {systemRoles.map(role => (
              <div key={role.id} data-testid={`role-${role.code}`} className="border border-stone-200 rounded-lg p-4 hover:shadow-md transition">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <span className="px-3 py-1 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-bold rounded-lg text-lg">{role.code}</span>
                    <div className="text-sm font-semibold text-stone-800 mt-2">{role.role_name}</div>
                    <div className="text-xs text-stone-500 mt-1">Created: {new Date(role.created_at).toLocaleDateString()}</div>
                  </div>
                  <Button onClick={() => handleDeleteRole(role.id, role.code)} data-testid={`delete-role-${role.code}`} variant="ghost" size="sm" className="text-red-600 hover:text-red-800 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-stone-500">
            <Settings className="w-12 h-12 mx-auto mb-3 text-stone-400" /><p>No roles defined</p>
          </div>
        )}
      </div>
    </TabsContent>
  );
}

export function BranchesTab({ ctx }) {
  const { branches, setShowCreateBranch, openEditBranch, deleteBranch } = ctx;

  return (
    <TabsContent value="branches">
      <div className="bg-white rounded-xl shadow-md border border-stone-200 p-6">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-stone-800">Branch Management</h2>
            <p className="text-sm text-stone-600 mt-1">Create and manage coffee shop branches with custom colors</p>
          </div>
          <Button onClick={() => setShowCreateBranch(true)} className="bg-gray-600 hover:bg-gray-700">
            <Plus className="w-4 h-4 mr-2" /> Add Branch
          </Button>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {branches.map(branch => (
            <div key={branch.id} className="border border-stone-200 rounded-lg p-4 hover:shadow-md transition">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded-full border border-stone-300" style={{ backgroundColor: branch.color }} />
                  <h3 className="font-semibold text-lg text-stone-800">{branch.name}</h3>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEditBranch(branch)}>Edit</Button>
                  <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => deleteBranch(branch.id)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
              <div className="text-sm text-stone-600 space-y-1">
                <div><span className="font-mono text-xs bg-stone-100 px-2 py-1 rounded">{branch.color}</span></div>
                {branch.cash_manager_branch_name && (
                  <div className="text-xs text-stone-500" data-testid={`branch-cash-alias-${branch.id}`}>
                    Cash Recon alias: <b className="text-stone-700">{branch.cash_manager_branch_name}</b>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        {branches.length === 0 && (
          <div className="text-center py-12 text-stone-500">
            <Coffee className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>No branches yet. Click "Add Branch" to create one.</p>
          </div>
        )}
      </div>
    </TabsContent>
  );
}

export function AuditTab({ ctx }) {
  const {
    auditLogs, auditDateFrom, setAuditDateFrom, auditDateTo, setAuditDateTo,
    fetchAuditLogs, handleExportAuditPDF
  } = ctx;

  return (
    <TabsContent value="audit">
      <div className="bg-white rounded-xl shadow-md border border-stone-200 p-6">
        <div className="mb-6 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-stone-800">Audit Log</h2>
            <p className="text-sm text-stone-600 mt-1">Track admin and manager activity</p>
          </div>
        </div>
        <div className="flex gap-4 mb-6 items-end">
          <div className="flex-1">
            <Label>From Date</Label>
            <Input type="date" value={auditDateFrom} onChange={(e) => setAuditDateFrom(e.target.value)} className="mt-1" />
          </div>
          <div className="flex-1">
            <Label>To Date</Label>
            <Input type="date" value={auditDateTo} onChange={(e) => setAuditDateTo(e.target.value)} className="mt-1" />
          </div>
          <Button onClick={fetchAuditLogs} disabled={!auditDateFrom || !auditDateTo} className="bg-gray-600 hover:bg-gray-700">
            <Search className="w-4 h-4 mr-2" /> Search
          </Button>
          <Button onClick={handleExportAuditPDF} disabled={!auditDateFrom || !auditDateTo || auditLogs.length === 0} className="bg-red-600 hover:bg-red-700">
            <FileText className="w-4 h-4 mr-2" /> Export PDF
          </Button>
        </div>
        {auditLogs.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-600 to-gray-700 text-white">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-sm">Timestamp</th>
                  <th className="px-4 py-3 text-left font-semibold text-sm">User</th>
                  <th className="px-4 py-3 text-center font-semibold text-sm">Role</th>
                  <th className="px-4 py-3 text-left font-semibold text-sm">Action</th>
                  <th className="px-4 py-3 text-center font-semibold text-sm">Entity</th>
                  <th className="px-4 py-3 text-left font-semibold text-sm">Details</th>
                </tr>
              </thead>
              <tbody>
                {auditLogs.map((log, index) => (
                  <tr key={log.id} className={`border-b border-stone-200 hover:bg-gray-50 transition ${index % 2 === 0 ? 'bg-white' : 'bg-stone-50'}`}>
                    <td className="px-4 py-3 text-sm text-stone-600">{new Date(log.timestamp).toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm font-medium text-stone-800">{log.username}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${log.user_role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>{log.user_role}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-stone-700">{log.action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</td>
                    <td className="px-4 py-3 text-center"><span className="px-2 py-1 bg-stone-200 text-stone-700 text-xs rounded">{log.entity_type}</span></td>
                    <td className="px-4 py-3 text-xs text-stone-600">
                      {Object.entries(log.details || {}).map(([key, value]) => (<div key={key}>{key}: {value}</div>))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-4 text-center text-sm text-stone-600">Showing {auditLogs.length} action{auditLogs.length !== 1 ? 's' : ''}</div>
          </div>
        ) : (
          <div className="text-center py-12 text-stone-500">
            {auditDateFrom && auditDateTo ? (
              <><FileText className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>No audit logs found for this date range</p></>
            ) : (
              <><Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" /><p>Select a date range to view audit logs</p></>
            )}
          </div>
        )}
      </div>
    </TabsContent>
  );
}
